import React, { useEffect, useRef, useState } from 'react';
import DateField from './DateField';
import './FilterPill.css';

export function FilterPill({ value, onChange, children, style, searchable, disabled, className }) {
  const [open,  setOpen]  = useState(false);
  const [query, setQuery] = useState('');
  const [dropPos, setDropPos] = useState(null);
  const wrapRef    = useRef(null);
  const triggerRef = useRef(null);
  const inputRef   = useRef(null);

  // Parse <option> children into plain objects
  const options = React.Children.toArray(children)
    .filter(React.isValidElement)
    .map(child => ({ value: child.props.value, label: child.props.children }));

  const placeholder  = options[0] ?? { value: '', label: '' };
  const realOptions  = options.slice(1);
  const selected     = options.find(o => o.value === value);
  const displayLabel = selected ? selected.label : placeholder.label;
  const isActive     = !!value;

  const q = query.trim().toLowerCase();
  const visibleOptions = q.length >= 2
    ? realOptions.filter(o => String(o.label).toLowerCase().includes(q))
    : realOptions;

  const showSearch = searchable || realOptions.length > 4;

  // Compute fixed-position coordinates from trigger bounding rect
  const computePos = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const idealH = Math.min(visibleOptions.length * 36 + (showSearch ? 44 : 8) + 16, 320);
    // Open downward only if there's enough room below, or if below has more room than above
    const openDown = spaceBelow >= idealH || spaceBelow >= spaceAbove;
    const availH   = openDown ? Math.max(spaceBelow, 80) : Math.max(spaceAbove, 80);
    setDropPos({
      top:       openDown ? rect.bottom + 6 : rect.top - Math.min(idealH, spaceAbove) - 6,
      left:      rect.left,
      minWidth:  Math.max(rect.width, 180),
      maxHeight: availH,
    });
  };

  const handleToggle = () => {
    if (!open) computePos();
    setOpen(o => !o);
  };

  // Reposition on scroll / resize while open
  useEffect(() => {
    if (!open) { setQuery(''); return; }
    const reposition = () => computePos();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);

    const outsideClick = (e) => {
      if (
        wrapRef.current && !wrapRef.current.contains(e.target) &&
        !e.target.closest('.fp-dropdown-fixed')
      ) setOpen(false);
    };
    document.addEventListener('mousedown', outsideClick);

    if (showSearch) setTimeout(() => inputRef.current?.focus(), 0);

    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      document.removeEventListener('mousedown', outsideClick);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, showSearch]);

  const handleSelect = (optValue) => {
    onChange({ target: { value: optValue } });
    setOpen(false);
  };

  return (
    <div className={`fp-wrap${isActive ? ' fp-wrap--active' : ''}${className ? ' ' + className : ''}`} ref={wrapRef} style={style}>
      <button
        ref={triggerRef}
        type="button"
        className={`fp-trigger${isActive ? ' fp-trigger--active' : ''}${disabled ? ' fp-trigger--disabled' : ''}`}
        onClick={disabled ? undefined : handleToggle}
        disabled={disabled}
      >
        <span className="fp-label">{displayLabel}</span>
        <svg
          className={`fp-chevron${open ? ' fp-chevron--open' : ''}`}
          width="11" height="11" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && dropPos && (
        <div
          className="fp-dropdown fp-dropdown-fixed"
          style={{ top: dropPos.top, left: dropPos.left, minWidth: dropPos.minWidth, maxHeight: dropPos.maxHeight, display: 'flex', flexDirection: 'column' }}
        >
          {showSearch && (
            <div className="fp-search-wrap" onMouseDown={e => e.stopPropagation()}>
              <svg className="fp-search-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                ref={inputRef}
                className="fp-search-input"
                type="text"
                placeholder="Cauta..."
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
              />
              {query && (
                <button className="fp-search-clear" onMouseDown={e => { e.stopPropagation(); setQuery(''); inputRef.current?.focus(); }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              )}
            </div>
          )}

          <div className="fp-options-list">
            <div
              className={`fp-option fp-option--placeholder${value === '' ? ' fp-option--selected' : ''}`}
              onMouseDown={() => handleSelect('')}
            >
              {value === '' && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
              <span>{placeholder.label}</span>
            </div>

            {visibleOptions.map(opt => (
              <div
                key={opt.value}
                className={`fp-option${opt.value === value ? ' fp-option--selected' : ''}`}
                onMouseDown={() => handleSelect(opt.value)}
              >
                {opt.value === value && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
                <span>{opt.label}</span>
              </div>
            ))}

            {q.length >= 2 && visibleOptions.length === 0 && (
              <div className="fp-no-results">Niciun rezultat</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function FilterDateRange({ from, to, onFromChange, onToChange }) {
  return (
    <div className="fp-date-wrap">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
      <DateField
        placeholder="de la"
        wrapperClassName="fp-date-df"
        className={`fp-date-input${from ? ' fp-date-input--active' : ''}`}
        value={from}
        onChange={v => onFromChange({ target: { value: v } })}
      />
      <span className="fp-date-sep">—</span>
      <DateField
        placeholder="pana la"
        wrapperClassName="fp-date-df"
        className={`fp-date-input${to ? ' fp-date-input--active' : ''}`}
        value={to}
        onChange={v => onToChange({ target: { value: v } })}
      />
    </div>
  );
}

export function FilterClearBtn({ onClick }) {
  return (
    <button className="fp-clear-btn" onClick={onClick} title="Reseteaza filtre">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
      Reset
    </button>
  );
}

export function FilterCountHint({ count, singular, plural }) {
  return (
    <span className="fp-count-hint">{count} {count === 1 ? singular : plural}</span>
  );
}
